/**
 * OpenMeta Admin — Onglet Analytics (Phase 5)
 *
 * - Funnel générateur avec conversions
 * - Bar chart verticales (recharts)
 * - Line chart waitlist par jour (recharts, fenêtre 7j fixe)
 * - Filtres période (7j/30j) et langue (fr/en/toutes)
 *
 * Toutes les données proviennent des routes JWT `/api/admin/analytics/*`.
 * Onglet FR uniquement (l'admin reste fr + light).
 */
import { useEffect, useMemo, useState } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  LineChart,
  Line,
} from 'recharts';
import { adminAnalyticsFunnel, adminAnalyticsVerticals, adminAnalyticsWaitlistDaily, adminAnalyticsOverview } from '@/lib/api';
import { VERTICALS } from '@/lib/verticals';
import { fr as FR_DICT } from '@/lib/i18n/fr';
import { ArrowRight, Users, Sparkles, Filter } from 'lucide-react';

const STEP_LABELS = {
  generator_view: 'Vue générateur',
  generate_success: 'Génération',
  unlock_shown: 'Teaser affiché',
  unlock_submitted: 'Unlock soumis',
  unlock_success: 'Waitlist',
};

const VERTICAL_COLORS = {
  sante: '#10B981',
  culture: '#8B5CF6',
  formation: '#0EA5E9',
  langues: '#F43F5E',
  social: '#F97316',
};

function formatDateShort(iso) {
  try {
    const d = new Date(iso + 'T00:00:00Z');
    return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
  } catch {
    return iso;
  }
}

function StatCard({ label, value, hint, accent = '#0A0E1A', testid }) {
  return (
    <div
      className="relative overflow-hidden rounded-2xl border border-[var(--om-border)] bg-white p-5"
      data-testid={testid}
    >
      <div
        aria-hidden
        className="absolute -right-8 -top-8 h-24 w-24 rounded-full opacity-40 blur-2xl"
        style={{ background: `radial-gradient(closest-side, ${accent}44, transparent 70%)` }}
      />
      <div className="relative text-xs font-semibold uppercase tracking-wider text-[var(--om-muted-2)]">
        {label}
      </div>
      <div className="relative mt-2 text-3xl font-extrabold tabular-nums text-[var(--om-ink)]">
        {value}
      </div>
      {hint && (
        <div className="relative mt-1 text-xs text-[var(--om-muted)]">{hint}</div>
      )}
    </div>
  );
}

function ChipButton({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
        active
          ? 'border-transparent bg-[var(--om-ink)] text-white'
          : 'border-[var(--om-border)] bg-white text-[var(--om-ink)] hover:bg-[var(--om-bg)]'
      }`}
      aria-pressed={active}
    >
      {children}
    </button>
  );
}

function FunnelStep({ step, index, isLast, total }) {
  const label = STEP_LABELS[step.event] || step.event;
  const pct = total > 0 ? Math.round((step.count / total) * 100) : 0;
  return (
    <div
      className="flex flex-1 flex-col rounded-2xl border border-[var(--om-border)] bg-white p-5"
      data-testid={`analytics-funnel-step-${step.event}`}
    >
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[var(--om-muted-2)]">
        <span
          className="inline-block h-2 w-2 rounded-full"
          style={{ background: index === 0 ? '#0A0E1A' : `hsl(${210 + index * 30} 85% 55%)` }}
        />
        Étape {index + 1}
      </div>
      <div className="mt-2 text-sm font-semibold text-[var(--om-ink)]">{label}</div>
      <div className="mt-4 flex items-baseline gap-2">
        <span className="text-3xl font-extrabold tabular-nums text-[var(--om-ink)]">{step.count}</span>
        <span className="text-xs font-medium text-[var(--om-muted)]">sessions</span>
      </div>
      {step.conversion_from_previous != null && (
        <div className="mt-2 flex items-center gap-1 text-xs text-[var(--om-muted)]">
          <ArrowRight size={12} strokeWidth={2.4} className="text-[var(--om-muted-2)]" />
          <span className="font-semibold text-[var(--om-ink)] tabular-nums">
            {step.conversion_from_previous.toFixed(1).replace('.0', '')}%
          </span>{' '}
          vs. précédente
        </div>
      )}
      {index === 0 && total > 0 && (
        <div className="mt-2 text-[10px] font-semibold uppercase tracking-wider text-[var(--om-muted-2)]">
          Base 100%
        </div>
      )}
      {index > 0 && total > 0 && (
        <div className="mt-2 text-[10px] font-semibold uppercase tracking-wider text-[var(--om-muted-2)]">
          {pct}% de la vue initiale
        </div>
      )}
    </div>
  );
}

export default function AnalyticsTab() {
  const [period, setPeriod] = useState(30);
  const [langFilter, setLangFilter] = useState('all');
  const [funnel, setFunnel] = useState(null);
  const [verticals, setVerticals] = useState(null);
  const [daily, setDaily] = useState(null);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const lang = langFilter === 'all' ? undefined : langFilter;
        const [f, v, o, d] = await Promise.all([
          adminAnalyticsFunnel({ period, lang }),
          adminAnalyticsVerticals({ period, lang }),
          adminAnalyticsOverview({ period, lang }),
          adminAnalyticsWaitlistDaily({ days: 7 }),
        ]);
        if (cancelled) return;
        setFunnel(f);
        setVerticals(v);
        setOverview(o);
        setDaily(d);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [period, langFilter]);

  const totalFunnelBase = funnel?.steps?.[0]?.count ?? 0;

  const verticalsChartData = useMemo(() => {
    const items = verticals?.items || [];
    return items.map((it) => ({
      code: it.vertical,
      label: FR_DICT.verticals[it.vertical]?.short || it.vertical,
      count: it.count,
      fill: VERTICAL_COLORS[it.vertical] || '#0A0E1A',
    }));
  }, [verticals]);

  const dailyChartData = useMemo(() => {
    const items = daily?.items || [];
    return items.map((it) => ({
      date: it.date,
      label: formatDateShort(it.date),
      count: it.count,
    }));
  }, [daily]);

  return (
    <div className="space-y-8" data-testid="admin-analytics-panel">
      {/* Filtres */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[var(--om-muted-2)]">
          <Filter size={12} strokeWidth={2.4} /> Période
        </div>
        <div className="flex items-center gap-1.5" data-testid="analytics-period-filter">
          <ChipButton active={period === 7} onClick={() => setPeriod(7)} testid="analytics-period-7">
            7 jours
          </ChipButton>
          <ChipButton active={period === 30} onClick={() => setPeriod(30)} testid="analytics-period-30">
            30 jours
          </ChipButton>
        </div>
        <div className="mx-2 hidden h-4 w-px bg-[var(--om-border)] sm:block" />
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[var(--om-muted-2)]">
          Langue
        </div>
        <div className="flex items-center gap-1.5" data-testid="analytics-lang-filter">
          <ChipButton active={langFilter === 'all'} onClick={() => setLangFilter('all')} testid="analytics-lang-all">
            Toutes
          </ChipButton>
          <ChipButton active={langFilter === 'fr'} onClick={() => setLangFilter('fr')} testid="analytics-lang-fr">
            FR
          </ChipButton>
          <ChipButton active={langFilter === 'en'} onClick={() => setLangFilter('en')} testid="analytics-lang-en">
            EN
          </ChipButton>
        </div>
        {loading && (
          <span className="text-xs text-[var(--om-muted-2)]" data-testid="analytics-loading">
            Chargement…
          </span>
        )}
      </div>

      {/* Overview */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatCard
          label="Sessions uniques"
          value={overview?.unique_sessions ?? '—'}
          hint={`Sur ${overview?.period_days ?? period} jours`}
          accent="#8B5CF6"
          testid="analytics-stat-sessions"
        />
        <StatCard
          label="Événements totaux"
          value={overview?.total_events ?? '—'}
          hint={langFilter === 'all' ? 'Toutes langues' : langFilter.toUpperCase()}
          accent="#0EA5E9"
          testid="analytics-stat-events"
        />
        <StatCard
          label="Conv. globale"
          value={funnel?.overall_conversion != null ? `${funnel.overall_conversion.toFixed(1).replace('.0', '')}%` : '—'}
          hint="Vue → Waitlist"
          accent="#10B981"
          testid="analytics-stat-conv"
        />
      </div>

      {/* Funnel */}
      <div>
        <div className="mb-4 flex items-center gap-2">
          <Users size={14} strokeWidth={2.4} className="text-[var(--om-muted-2)]" />
          <h2 className="text-sm font-semibold uppercase tracking-wider text-[var(--om-muted-2)]">
            Funnel générateur
          </h2>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5" data-testid="analytics-funnel">
          {(funnel?.steps || []).map((step, i, arr) => (
            <FunnelStep
              key={step.event}
              step={step}
              index={i}
              isLast={i === arr.length - 1}
              total={totalFunnelBase}
            />
          ))}
        </div>
      </div>

      {/* Verticales + Waitlist daily */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="rounded-2xl border border-[var(--om-border)] bg-white p-6" data-testid="analytics-verticals-card">
          <div className="mb-4 flex items-center gap-2">
            <Sparkles size={14} strokeWidth={2.4} className="text-[var(--om-muted-2)]" />
            <h2 className="text-sm font-semibold uppercase tracking-wider text-[var(--om-muted-2)]">
              Générations par verticale
            </h2>
          </div>
          <div className="h-[260px]" data-testid="analytics-verticals-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={verticalsChartData} margin={{ top: 8, right: 8, bottom: 8, left: -12 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E6E9EF" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 12, fill: '#5B6274' }} axisLine={false} tickLine={false} />
                <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: '#8892A6' }} axisLine={false} tickLine={false} />
                <Tooltip
                  cursor={{ fill: 'rgba(10,14,26,0.04)' }}
                  contentStyle={{ borderRadius: 12, border: '1px solid #E6E9EF', fontSize: 12 }}
                />
                <Bar dataKey="count" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-3 flex flex-wrap gap-3">
            {verticalsChartData.map((v) => (
              <div key={v.code} className="flex items-center gap-1.5 text-xs text-[var(--om-muted)]">
                <span className="h-2 w-2 rounded-full" style={{ background: v.fill }} />
                {v.label} <span className="tabular-nums font-semibold text-[var(--om-ink)]">{v.count}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-2xl border border-[var(--om-border)] bg-white p-6" data-testid="analytics-waitlist-daily-card">
          <div className="mb-4 flex items-center gap-2">
            <Users size={14} strokeWidth={2.4} className="text-[var(--om-muted-2)]" />
            <h2 className="text-sm font-semibold uppercase tracking-wider text-[var(--om-muted-2)]">
              Waitlist par jour (7 derniers jours)
            </h2>
          </div>
          <div className="h-[260px]" data-testid="analytics-waitlist-chart">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={dailyChartData} margin={{ top: 8, right: 12, bottom: 8, left: -12 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E6E9EF" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 12, fill: '#5B6274' }} axisLine={false} tickLine={false} />
                <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: '#8892A6' }} axisLine={false} tickLine={false} />
                <Tooltip
                  cursor={{ stroke: '#8B5CF6', strokeWidth: 1 }}
                  contentStyle={{ borderRadius: 12, border: '1px solid #E6E9EF', fontSize: 12 }}
                />
                <Line
                  type="monotone"
                  dataKey="count"
                  stroke="#0A0E1A"
                  strokeWidth={2.5}
                  dot={{ r: 4, stroke: '#0A0E1A', fill: '#ffffff', strokeWidth: 2 }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-3 text-xs text-[var(--om-muted)]">
            Total 7 j :{' '}
            <span className="font-semibold text-[var(--om-ink)] tabular-nums">
              {daily?.total ?? 0}
            </span>{' '}
            inscription{(daily?.total || 0) > 1 ? 's' : ''}
          </div>
        </div>
      </div>
    </div>
  );
}
